import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import WidgetsIcon from "@mui/icons-material/Widgets";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import MoveUpIcon from "@mui/icons-material/MoveUp";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import HelpCenterIcon from "@mui/icons-material/HelpCenter";
import LibraryAddCheckIcon from "@mui/icons-material/LibraryAddCheck";
import SchemaIcon from "@mui/icons-material/Schema";
import AssessmentIcon from "@mui/icons-material/Assessment";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import PaymentsIcon from "@mui/icons-material/Payments";
import NotificationsIcon from "@mui/icons-material/Notifications";
import PlaceIcon from "@mui/icons-material/Place";
import ExtensionIcon from "@mui/icons-material/Extension";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import ImageSearchIcon from "@mui/icons-material/ImageSearch";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import SettingsIcon from "@mui/icons-material/Settings";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import CalculateIcon from "@mui/icons-material/Calculate";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ChatIcon from "@mui/icons-material/Chat";
import { Groups2Outlined } from "@mui/icons-material";

// Slug + label + group come from the shared single source of truth
// (shared/pageSlugs.json). The frontend `copy:shared` npm script copies that file
// into src/generated/ before start/build/test, because CRA cannot import from
// outside src/. To add/remove a page, edit shared/pageSlugs.json — mobile-staff
// reads the same JSON, so the two stay in sync automatically.
import PAGE_SLUGS from "../generated/pageSlugs.json";

/**
 * Icons are presentation-only and stay here (JSX can't live in JSON), keyed by slug.
 * A page slug with no icon falls back to no icon (Sidebar handles a missing icon).
 */
const PAGE_ICONS = {
  scan: <QrCodeScannerIcon />,
  tracking: <TravelExploreIcon />,
  packages: <WidgetsIcon />,
  customers: <Groups2Outlined />,
  whatsapp: <WhatsAppIcon />,
  unassignedpackage: <HelpCenterIcon />,
  matchpackage: <LibraryAddCheckIcon />,
  bulkmove: <MoveUpIcon />,
  departurebatch: <LocalShippingOutlinedIcon />,
  packagebulkmove: <SwapHorizIcon />,
  "parcel-monitoring": <ReportProblemIcon />,
  gallery: <PhotoLibraryIcon />,
  notifications: <NotificationsIcon />,
  "wallet/accounts": <PersonSearchIcon />,
  "wallet/withdrawal": <AccountBalanceIcon />,
  "wallet/report": <AssessmentIcon />,
  financial: <AssessmentIcon />,
  "financial/approvals": <FactCheckIcon />,
  "slipbot-history": <ImageSearchIcon />,
  "branch-cash": <PaymentsIcon />,
  whatsappbulk: <WhatsAppIcon />,
  whatsappchat: <ChatIcon />,
  "sales-documents": <ReceiptLongIcon />,
  users: <HomeOutlinedIcon />,
  "agent-settings": <Groups2Outlined />,
  stations: <PlaceIcon />,
  whatsappgroup: <SchemaIcon />,
  settings: <SettingsIcon />,
  "chrome-extension": <ExtensionIcon />,
};

/**
 * Centralized definition of all staff portal pages.
 * Single source of truth for: page slug (routes, PermittedRoute, permission checks),
 * display label, group (Sidebar + PagePermissionEditor sections), and icon (Sidebar).
 *
 * To add a new page: add an entry to shared/pageSlugs.json (+ an icon above).
 * admin role bypasses all page checks — always has full access.
 */
export const ALL_PAGES = PAGE_SLUGS.map((p) => ({
  slug: p.slug,
  label: p.label,
  group: p.group,
  icon: PAGE_ICONS[p.slug] ?? null,
  tabs: p.tabs ?? null,
}));

/** Unique ordered list of groups */
export const PAGE_GROUPS = [...new Set(ALL_PAGES.map((p) => p.group))];

/**
 * Pages every logged-in staff user can open, with no permission behind them.
 * Deliberately NOT in shared/pageSlugs.json: they must not appear in the role
 * editor as something to grant/revoke. Sidebar renders them inside their group;
 * their routes in App.js skip PermittedRoute.
 */
export const ALWAYS_VISIBLE_PAGES = [
  { slug: "calculator", label: "Shipping Calculator", group: "General Management", icon: <CalculateIcon />, tabs: null },
];

const ALWAYS_VISIBLE_SLUGS = new Set(ALWAYS_VISIBLE_PAGES.map((p) => p.slug));

/** Everything the nav can show (permission registry + always-visible pages). */
export const NAV_PAGES = [...ALL_PAGES, ...ALWAYS_VISIBLE_PAGES];

/** Check whether a user has access to a given page slug */
export const hasPageAccess = (userObj, slug) => {
  if (!userObj) return false;
  if (ALWAYS_VISIBLE_SLUGS.has(slug)) return true;
  if (userObj.role === "admin") return true;
  return Array.isArray(userObj.pages) && userObj.pages.includes(slug);
};

/**
 * Which tabs of a given page a user may see.
 * admin -> all tabs. Everyone else -> only tabs whose slug is explicitly
 * granted in userObj.pages (no "parent slug implies all tabs" fallback —
 * see backend/scripts/migrate-add-tab-slugs.js for the one-time backfill
 * that grants existing roles their tab slugs).
 * Returns [] for a page with no tabs defined.
 */
export const getVisibleTabs = (userObj, pageSlug) => {
  const page = ALL_PAGES.find((p) => p.slug === pageSlug);
  if (!page?.tabs) return [];
  if (userObj?.role === "admin") return page.tabs;
  if (!userObj || !Array.isArray(userObj.pages)) return [];
  return page.tabs.filter((t) => userObj.pages.includes(t.slug));
};
